
import React, { useState } from 'react';
import { Check, Copy } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import PromptUsageMeter from './PromptUsageMeter';
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

// Feature flag for upgrade functionality - Set to false for alpha launch
const ENABLE_UPGRADES = false; // Set to false for alpha launch

interface PromptUpgradeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  usageInfo?: {
    canCopy: boolean;
    usedCount: number;
    limit: number;
    percentUsed: number;
  } | null;
}

const PromptUpgradeDialog: React.FC<PromptUpgradeDialogProps> = ({ 
  open, 
  onOpenChange,
  usageInfo
}) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [email, setEmail] = useState("");
  const [isInterested, setIsInterested] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const handleSubmitInterest = async () => {
    if (!user) {
      toast.error("You need to be signed in to express interest");
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Save interest to database
      const { error } = await supabase
        .from('premium_interest')
        .insert({
          user_id: user.id,
          email: email || user.email
        });
      
      if (error) throw error;
      
      toast.success("Thanks for your interest! We'll notify you when premium features are available.");
      setIsInterested(true);
    } catch (err) {
      console.error("Error recording premium interest:", err);
      toast.error("Failed to record your interest. Please try again later.");
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // If upgrades are disabled, show a modified dialog for alpha testers
  if (!ENABLE_UPGRADES) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Alpha Testing Mode</DialogTitle>
            <DialogDescription>
              We are in alpha mode and when we move to beta, you will be able to access premium options. If you wish to get early access to the premium features, please select 'Yes' below.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            {!isInterested ? (
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground mb-4">
                  Would you be interested in premium features when we launch? Let us know so we can keep you updated!
                </p>
                <Input 
                  type="email"
                  placeholder="Your email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
                <div className="flex space-x-2">
                  <Button 
                    variant="outline" 
                    onClick={() => onOpenChange(false)}
                    className="flex-1"
                  >
                    No thanks
                  </Button>
                  <Button 
                    variant="default"
                    onClick={handleSubmitInterest}
                    disabled={isSubmitting}
                    className="flex-1"
                  >
                    Yes, I'm interested
                  </Button>
                </div>
              </div>
            ) : (
              <div className="text-center py-2">
                <Check className="h-10 w-10 text-green-500 mx-auto mb-2" />
                <p className="text-sm font-medium">Thanks for your interest!</p>
                <Button 
                  variant="default"
                  onClick={() => onOpenChange(false)}
                  className="mt-4"
                >
                  Continue Exploring
                </Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    );
  }
  
  // Regular upgrade dialog for when feature is enabled
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Upgrade Your Account</DialogTitle>
          <DialogDescription>
            You've reached your monthly limit of {usageInfo?.limit} prompt uses.
            Upgrade to a premium plan to get more prompts and additional features.
          </DialogDescription>
        </DialogHeader>
        
        {usageInfo && (
          <div className="mb-4">
            <PromptUsageMeter 
              usedCount={usageInfo.usedCount}
              limit={usageInfo.limit}
              percentUsed={usageInfo.percentUsed}
            />
          </div>
        )}
        
        <div className="flex flex-col space-y-4 py-4">
          <div className="flex items-center gap-4 p-4 border rounded-lg">
            <div className="bg-primary/10 p-3 rounded-full">
              <Copy className="h-5 w-5 text-primary" />
            </div>
            <div className="flex-1">
              <h4 className="font-medium">Pro Plan</h4>
              <p className="text-sm text-muted-foreground">Up to 100 prompt uses per month</p>
            </div>
            <div className="font-semibold">$9.99</div>
          </div>
          
          <div className="flex items-center gap-4 p-4 border rounded-lg">
            <div className="bg-primary/10 p-3 rounded-full">
              <Copy className="h-5 w-5 text-primary" />
            </div>
            <div className="flex-1">
              <h4 className="font-medium">Enterprise Plan</h4>
              <p className="text-sm text-muted-foreground">Unlimited prompt uses</p>
            </div>
            <div className="font-semibold">$29.99</div>
          </div>
        </div>
        <DialogFooter>
          <Button 
            variant="outline" 
            onClick={() => onOpenChange(false)}
          >
            Maybe Later
          </Button>
          <Button 
            variant="default"
            onClick={() => {
              onOpenChange(false);
              navigate('/pricing');
            }}
          >
            View Plans
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PromptUpgradeDialog;
